import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSeekerProfile, createOrUpdateSeekerProfile } from '../services/seekerProfileService';
import { getCompanyProfileByUserId, updateCompanyProfile } from '../services/companyService';
import { getUserData } from '../services/userService';
import LoadingSpinner from '../components/LoadingSpinner';

const AdminUserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState(null);

  useEffect(() => {
    fetchUserProfile();
  }, [userId]);

  const fetchUserProfile = async () => {
    setLoading(true);
    try {
      // Fetch user data
      const userDataResult = await getUserData(userId);
      if (userDataResult) {
        setUserData(userDataResult);

        // Fetch profile based on user type
        if (userDataResult.userType === 'jobseeker') {
          const result = await getSeekerProfile(userId);

          // Initialize default empty profile structure
          const emptyProfile = {
            personalInfo: {
              firstName: userDataResult.firstName || '',
              lastName: userDataResult.lastName || '',
              email: userDataResult.email || '',
              phone: userDataResult.phone || '',
              dateOfBirth: '',
              gender: '',
              location: '',
              linkedIn: '',
              portfolio: ''
            },
            professionalSummary: '',
            experience: [],
            education: [],
            skills: [],
            certifications: [],
            languages: [],
            preferences: {
              careerLevel: '',
              expectedSalary: {
                min: '',
                max: '',
                currency: 'INR'
              },
              jobTypes: [],
              workModes: [],
              preferredLocations: '',
              targetRoles: [],
              noticePeriod: '',
              willingToRelocate: false
            },
            resumeURL: '',
            resumeFileName: ''
          };

          if (result.success && result.profile) {
            // Merge existing profile with empty structure to ensure all fields exist
            const mergedProfile = {
              ...emptyProfile,
              ...result.profile,
              personalInfo: {
                ...emptyProfile.personalInfo,
                ...result.profile.personalInfo
              },
              preferences: {
                ...emptyProfile.preferences,
                ...result.profile.preferences,
                expectedSalary: {
                  ...emptyProfile.preferences.expectedSalary,
                  ...result.profile.preferences?.expectedSalary
                }
              }
            };
            setProfile(mergedProfile);
            setEditedProfile(mergedProfile);
          } else {
            // Use empty profile if no profile exists
            setProfile(emptyProfile);
            setEditedProfile(emptyProfile);
          }
        } else if (userDataResult.userType === 'employer') {
          const result = await getCompanyProfileByUserId(userId);
          if (result.success && result.data) {
            setProfile(result.data);
            setEditedProfile(result.data);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (userData.userType === 'jobseeker') {
        const result = await createOrUpdateSeekerProfile(userId, editedProfile);
        if (result.success) {
          setProfile(editedProfile);
          setIsEditing(false);
          alert('Profile updated successfully!');
        } else {
          alert('Failed to update profile: ' + result.error);
        }
      } else if (userData.userType === 'employer') {
        const result = await updateCompanyProfile(userId, editedProfile);
        if (result.success) {
          setProfile(editedProfile);
          setIsEditing(false);
          alert('Company profile updated successfully!');
        } else {
          alert('Failed to update profile: ' + result.error);
        }
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setEditedProfile(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleArrayAdd = (field, newItem) => {
    setEditedProfile(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), newItem]
    }));
  };

  const handleArrayRemove = (field, index) => {
    setEditedProfile(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleArrayUpdate = (field, index, updatedItem) => {
    setEditedProfile(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? updatedItem : item)
    }));
  };

  if (loading) return <LoadingSpinner />;

  if (!userData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">User Not Found</h2>
          <button
            onClick={() => navigate('/superadmin/users')}
            className="text-blue-600 hover:underline"
          >
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/superadmin/users')}
                className="text-gray-600 hover:text-gray-900"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {userData.firstName} {userData.lastName}
                </h1>
                <p className="text-sm text-gray-500">
                  {userData.userType?.charAt(0).toUpperCase() + userData.userType?.slice(1)} Profile
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isEditing ? (
                <>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setEditedProfile(profile);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Edit Profile
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* User Info Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">User Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Email</label>
              <p className="text-gray-900">{userData.email}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Phone</label>
              <p className="text-gray-900">{userData.phone || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">User ID</label>
              <p className="text-gray-900 font-mono text-sm">{userId}</p>
            </div>
          </div>
        </div>

        {/* Job Seeker Profile */}
        {userData.userType === 'jobseeker' && (
          <div className="space-y-6">
            {/* Personal Info */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">First Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.personalInfo?.firstName || ''}
                      onChange={(e) => handleInputChange('personalInfo', 'firstName', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile?.personalInfo?.firstName || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Last Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.personalInfo?.lastName || ''}
                      onChange={(e) => handleInputChange('personalInfo', 'lastName', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile?.personalInfo?.lastName || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Email</label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={editedProfile?.personalInfo?.email || ''}
                      onChange={(e) => handleInputChange('personalInfo', 'email', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile?.personalInfo?.email || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Phone</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={editedProfile?.personalInfo?.phone || ''}
                      onChange={(e) => handleInputChange('personalInfo', 'phone', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile?.personalInfo?.phone || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Date of Birth</label>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editedProfile?.personalInfo?.dateOfBirth || ''}
                      onChange={(e) => handleInputChange('personalInfo', 'dateOfBirth', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile?.personalInfo?.dateOfBirth || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Gender</label>
                  {isEditing ? (
                    <select
                      value={editedProfile?.personalInfo?.gender || ''}
                      onChange={(e) => handleInputChange('personalInfo', 'gender', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  ) : (
                    <p className="mt-1 text-gray-900">{profile?.personalInfo?.gender || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Location</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.personalInfo?.location || ''}
                      onChange={(e) => handleInputChange('personalInfo', 'location', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile?.personalInfo?.location || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">LinkedIn</label>
                  {isEditing ? (
                    <input
                      type="url"
                      value={editedProfile?.personalInfo?.linkedIn || ''}
                      onChange={(e) => handleInputChange('personalInfo', 'linkedIn', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile?.personalInfo?.linkedIn || 'N/A'}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700">Portfolio</label>
                  {isEditing ? (
                    <input
                      type="url"
                      value={editedProfile?.personalInfo?.portfolio || ''}
                      onChange={(e) => handleInputChange('personalInfo', 'portfolio', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile?.personalInfo?.portfolio || 'N/A'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Professional Summary */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Professional Summary</h2>
              {isEditing ? (
                <textarea
                  value={editedProfile?.professionalSummary || ''}
                  onChange={(e) => setEditedProfile(prev => ({ ...prev, professionalSummary: e.target.value }))}
                  rows={4}
                  placeholder="Write a brief professional summary..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="text-gray-900">{profile?.professionalSummary || 'N/A'}</p>
              )}
            </div>

            {/* Experience */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Experience</h2>
                {isEditing && (
                  <button
                    onClick={() => handleArrayAdd('experience', {
                      jobTitle: '',
                      company: '',
                      startDate: '',
                      endDate: '',
                      isCurrentJob: false,
                      yearsOfExperience: '',
                      months: '',
                      description: ''
                    })}
                    className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                  >
                    + Add Experience
                  </button>
                )}
              </div>
              <div className="space-y-4">
                {editedProfile?.experience?.length > 0 ? (
                  editedProfile.experience.map((exp, index) => (
                    <div key={index} className="border-l-4 border-blue-500 pl-4 py-2 bg-blue-50 rounded-r-lg">
                      {isEditing ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <input
                              type="text"
                              placeholder="Job Title"
                              value={exp.jobTitle || ''}
                              onChange={(e) => handleArrayUpdate('experience', index, { ...exp, jobTitle: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                            <input
                              type="text"
                              placeholder="Company"
                              value={exp.company || ''}
                              onChange={(e) => handleArrayUpdate('experience', index, { ...exp, company: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                            <input
                              type="text"
                              placeholder="Start Date (e.g., Jan 2020)"
                              value={exp.startDate || ''}
                              onChange={(e) => handleArrayUpdate('experience', index, { ...exp, startDate: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                            <input
                              type="text"
                              placeholder="End Date (e.g., Dec 2022)"
                              value={exp.endDate || ''}
                              onChange={(e) => handleArrayUpdate('experience', index, { ...exp, endDate: e.target.value })}
                              disabled={exp.isCurrentJob}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                            />
                            <input
                              type="number"
                              placeholder="Years of Experience"
                              value={exp.yearsOfExperience || ''}
                              onChange={(e) => handleArrayUpdate('experience', index, { ...exp, yearsOfExperience: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                            <input
                              type="number"
                              placeholder="Months"
                              value={exp.months || ''}
                              onChange={(e) => handleArrayUpdate('experience', index, { ...exp, months: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={exp.isCurrentJob || false}
                              onChange={(e) => handleArrayUpdate('experience', index, { ...exp, isCurrentJob: e.target.checked, endDate: e.target.checked ? '' : exp.endDate })}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Currently working here</span>
                          </label>
                          <textarea
                            placeholder="Job Description"
                            value={exp.description || ''}
                            onChange={(e) => handleArrayUpdate('experience', index, { ...exp, description: e.target.value })}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            onClick={() => handleArrayRemove('experience', index)}
                            className="text-red-600 text-sm hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <>
                          <p className="font-semibold text-gray-900">{exp.jobTitle || 'N/A'}</p>
                          <p className="text-gray-600">{exp.company || 'N/A'}</p>
                          <p className="text-sm text-gray-500 mt-2">
                            {exp.startDate} - {exp.isCurrentJob ? 'Present' : exp.endDate}
                            {exp.yearsOfExperience && ` • ${exp.yearsOfExperience} years`}
                            {exp.months && ` ${exp.months} months`}
                          </p>
                          {exp.description && (
                            <p className="text-sm text-gray-700 mt-2">{exp.description}</p>
                          )}
                        </>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">No experience added yet</p>
                )}
              </div>
            </div>

            {/* Education */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Education</h2>
                {isEditing && (
                  <button
                    onClick={() => handleArrayAdd('education', {
                      degree: '',
                      fieldOfStudy: '',
                      institution: '',
                      year: '',
                      grade: ''
                    })}
                    className="px-3 py-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"
                  >
                    + Add Education
                  </button>
                )}
              </div>
              <div className="space-y-4">
                {editedProfile?.education?.length > 0 ? (
                  editedProfile.education.map((edu, index) => (
                    <div key={index} className="border-l-4 border-green-500 pl-4 py-2 bg-green-50 rounded-r-lg">
                      {isEditing ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <input
                              type="text"
                              placeholder="Degree (e.g., B.Tech)"
                              value={edu.degree || ''}
                              onChange={(e) => handleArrayUpdate('education', index, { ...edu, degree: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                            />
                            <input
                              type="text"
                              placeholder="Field of Study"
                              value={edu.fieldOfStudy || ''}
                              onChange={(e) => handleArrayUpdate('education', index, { ...edu, fieldOfStudy: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                            />
                            <input
                              type="text"
                              placeholder="Institution"
                              value={edu.institution || ''}
                              onChange={(e) => handleArrayUpdate('education', index, { ...edu, institution: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                            />
                            <input
                              type="text"
                              placeholder="Year (e.g., 2020)"
                              value={edu.year || ''}
                              onChange={(e) => handleArrayUpdate('education', index, { ...edu, year: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                            />
                            <input
                              type="text"
                              placeholder="Grade/CGPA"
                              value={edu.grade || ''}
                              onChange={(e) => handleArrayUpdate('education', index, { ...edu, grade: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                            />
                          </div>
                          <button
                            onClick={() => handleArrayRemove('education', index)}
                            className="text-red-600 text-sm hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <>
                          <p className="font-semibold text-gray-900">{edu.degree || 'N/A'}</p>
                          <p className="text-gray-600">{edu.institution || 'N/A'}</p>
                          <p className="text-sm text-gray-500 mt-2">
                            {edu.fieldOfStudy} • {edu.year}
                            {edu.grade && ` • Grade: ${edu.grade}`}
                          </p>
                        </>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">No education added yet</p>
                )}
              </div>
            </div>

            {/* Skills */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Skills</h2>
              {isEditing ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Add skills separated by commas (e.g., React, Node.js, Python)"
                    value={editedProfile?.skills?.join(', ') || ''}
                    onChange={(e) => setEditedProfile(prev => ({ ...prev, skills: e.target.value.split(',').map(s => s.trim()).filter(s => s) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500">Separate skills with commas</p>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {profile?.skills?.length > 0 ? (
                    profile.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                      >
                        {skill}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">No skills added yet</p>
                  )}
                </div>
              )}
            </div>

            {/* Languages */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Languages</h2>
                {isEditing && (
                  <button
                    onClick={() => handleArrayAdd('languages', { language: '', proficiency: '' })}
                    className="px-3 py-1 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700"
                  >
                    + Add Language
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {editedProfile?.languages?.length > 0 ? (
                  editedProfile.languages.map((lang, index) => (
                    <div key={index} className="flex gap-3 items-center">
                      {isEditing ? (
                        <>
                          <input
                            type="text"
                            placeholder="Language"
                            value={lang.language || ''}
                            onChange={(e) => handleArrayUpdate('languages', index, { ...lang, language: e.target.value })}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          />
                          <select
                            value={lang.proficiency || ''}
                            onChange={(e) => handleArrayUpdate('languages', index, { ...lang, proficiency: e.target.value })}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">Select Proficiency</option>
                            <option value="Basic">Basic</option>
                            <option value="Intermediate">Intermediate</option>
                            <option value="Fluent">Fluent</option>
                            <option value="Native">Native</option>
                          </select>
                          <button
                            onClick={() => handleArrayRemove('languages', index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </>
                      ) : (
                        <p className="text-gray-900">
                          {lang.language} <span className="text-purple-600 font-medium">({lang.proficiency})</span>
                        </p>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">No languages added yet</p>
                )}
              </div>
            </div>

            {/* Certifications */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Certifications</h2>
                {isEditing && (
                  <button
                    onClick={() => handleArrayAdd('certifications', { name: '', issuer: '', year: '' })}
                    className="px-3 py-1 bg-orange-600 text-white rounded-md text-sm hover:bg-orange-700"
                  >
                    + Add Certification
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {editedProfile?.certifications?.length > 0 ? (
                  editedProfile.certifications.map((cert, index) => (
                    <div key={index} className="border-l-4 border-orange-500 pl-4 py-2 bg-orange-50 rounded-r-lg">
                      {isEditing ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            placeholder="Certification Name"
                            value={cert.name || ''}
                            onChange={(e) => handleArrayUpdate('certifications', index, { ...cert, name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              placeholder="Issuer"
                              value={cert.issuer || ''}
                              onChange={(e) => handleArrayUpdate('certifications', index, { ...cert, issuer: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                            />
                            <input
                              type="text"
                              placeholder="Year"
                              value={cert.year || ''}
                              onChange={(e) => handleArrayUpdate('certifications', index, { ...cert, year: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                            />
                          </div>
                          <button
                            onClick={() => handleArrayRemove('certifications', index)}
                            className="text-red-600 text-sm hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <>
                          <p className="font-semibold text-gray-900">{cert.name}</p>
                          <p className="text-sm text-gray-600">{cert.issuer} • {cert.year}</p>
                        </>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">No certifications added yet</p>
                )}
              </div>
            </div>

            {/* Preferences */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Job Preferences</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Career Level</label>
                  {isEditing ? (
                    <select
                      value={editedProfile?.preferences?.careerLevel || ''}
                      onChange={(e) => handleInputChange('preferences', 'careerLevel', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Level</option>
                      <option value="fresher">Fresher</option>
                      <option value="entry">Entry Level</option>
                      <option value="mid">Mid Level</option>
                      <option value="senior">Senior Level</option>
                      <option value="lead">Lead</option>
                    </select>
                  ) : (
                    <p className="mt-1 text-gray-900">{profile?.preferences?.careerLevel || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Notice Period</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.preferences?.noticePeriod || ''}
                      onChange={(e) => handleInputChange('preferences', 'noticePeriod', e.target.value)}
                      placeholder="e.g., Immediate, 15 days, 1 month"
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile?.preferences?.noticePeriod || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Min Expected Salary (₹)</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.preferences?.expectedSalary?.min || ''}
                      onChange={(e) => setEditedProfile(prev => ({
                        ...prev,
                        preferences: {
                          ...prev.preferences,
                          expectedSalary: { ...prev.preferences?.expectedSalary, min: e.target.value }
                        }
                      }))}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile?.preferences?.expectedSalary?.min || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Max Expected Salary (₹)</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.preferences?.expectedSalary?.max || ''}
                      onChange={(e) => setEditedProfile(prev => ({
                        ...prev,
                        preferences: {
                          ...prev.preferences,
                          expectedSalary: { ...prev.preferences?.expectedSalary, max: e.target.value }
                        }
                      }))}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile?.preferences?.expectedSalary?.max || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Preferred Locations</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.preferences?.preferredLocations || ''}
                      onChange={(e) => handleInputChange('preferences', 'preferredLocations', e.target.value)}
                      placeholder="e.g., Mumbai, Pune, Bangalore"
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile?.preferences?.preferredLocations || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Willing to Relocate</label>
                  {isEditing ? (
                    <select
                      value={editedProfile?.preferences?.willingToRelocate ? 'true' : 'false'}
                      onChange={(e) => handleInputChange('preferences', 'willingToRelocate', e.target.value === 'true')}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  ) : (
                    <p className="mt-1 text-gray-900">{profile?.preferences?.willingToRelocate ? 'Yes' : 'No'}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700">Job Types</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.preferences?.jobTypes?.join(', ') || ''}
                      onChange={(e) => setEditedProfile(prev => ({
                        ...prev,
                        preferences: {
                          ...prev.preferences,
                          jobTypes: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                        }
                      }))}
                      placeholder="e.g., Full-time, Part-time, Contract"
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile?.preferences?.jobTypes?.join(', ') || 'N/A'}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700">Work Modes</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.preferences?.workModes?.join(', ') || ''}
                      onChange={(e) => setEditedProfile(prev => ({
                        ...prev,
                        preferences: {
                          ...prev.preferences,
                          workModes: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                        }
                      }))}
                      placeholder="e.g., Work From Home, Work From Office, Hybrid"
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile?.preferences?.workModes?.join(', ') || 'N/A'}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700">Target Roles</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.preferences?.targetRoles?.join(', ') || ''}
                      onChange={(e) => setEditedProfile(prev => ({
                        ...prev,
                        preferences: {
                          ...prev.preferences,
                          targetRoles: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                        }
                      }))}
                      placeholder="e.g., Software Engineer, Data Analyst"
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile?.preferences?.targetRoles?.join(', ') || 'N/A'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Resume */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Resume</h2>
              {profile?.resumeURL ? (
                <div className="flex items-center gap-3">
                  <a
                    href={profile.resumeURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {profile.resumeFileName || 'View Resume'}
                  </a>
                  <span className="text-gray-400">•</span>
                  <a
                    href={profile.resumeURL}
                    download
                    className="text-blue-600 hover:underline"
                  >
                    Download
                  </a>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No resume uploaded</p>
              )}
            </div>
          </div>
        )}

        {/* Employer Profile */}
        {userData.userType === 'employer' && profile && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Company Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.companyName || ''}
                      onChange={(e) => setEditedProfile(prev => ({ ...prev, companyName: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile.companyName || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Industry</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.industry || ''}
                      onChange={(e) => setEditedProfile(prev => ({ ...prev, industry: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile.industry || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Company Size</label>
                  {isEditing ? (
                    <select
                      value={editedProfile?.companySize || ''}
                      onChange={(e) => setEditedProfile(prev => ({ ...prev, companySize: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select size</option>
                      <option value="1-10">1-10 employees</option>
                      <option value="11-50">11-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="201-500">201-500 employees</option>
                      <option value="501-1000">501-1000 employees</option>
                      <option value="1000+">1000+ employees</option>
                    </select>
                  ) : (
                    <p className="mt-1 text-gray-900">{profile.companySize || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Location</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.location || ''}
                      onChange={(e) => setEditedProfile(prev => ({ ...prev, location: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile.location || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Website</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.website || ''}
                      onChange={(e) => setEditedProfile(prev => ({ ...prev, website: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile.website || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Founded Year</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.foundedYear || ''}
                      onChange={(e) => setEditedProfile(prev => ({ ...prev, foundedYear: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile.foundedYear || 'N/A'}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700">Description</label>
                  {isEditing ? (
                    <textarea
                      value={editedProfile?.description || ''}
                      onChange={(e) => setEditedProfile(prev => ({ ...prev, description: e.target.value }))}
                      rows={4}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{profile.description || 'N/A'}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUserProfile;
